import PropTypes from 'prop-types';
import { useState } from 'react';

import Block from 'shopper/components/Block';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Icon from 'shopper/components/Icon';

import { sendEvent } from 'lib/gtag';

import PartnerMessage from './PartnerMessage';
import ProductItem from './ProductItem';
import Wrapper from './Wrapper';

import COMPARAI from 'constants/comparai';
import LINKS from 'constants/links';

const COMPARAI_URL_WITH_SEARCH_QUERY = `${LINKS.COMPARAI}${COMPARAI.SEARCH_QUERY_PARAM}`;
const DEFAULT_PRODUCTS_LIMIT = 3;
const NOT_EXPANDED_PRODUCTS_LIMIT = 1;

const toProductsWithLimit = (products, maxItems) =>
  products.length > maxItems ? products.slice(0, maxItems) : products;

const ProductList = ({
  gaEventCategory,
  products,
  productSlug,
  utmMedium,
  expandable,
  subtitle,
  partnerMessageOnBottom,
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxItems, setMaxItems] = useState(
    expandable ? NOT_EXPANDED_PRODUCTS_LIMIT : DEFAULT_PRODUCTS_LIMIT
  );

  const onShowAllItems = () => {
    setIsExpanded(true);
    setMaxItems(DEFAULT_PRODUCTS_LIMIT);

    sendEvent({
      action: 'view_all_offers',
      category: gaEventCategory,
    });
  };

  return (
    <Block>
      {!partnerMessageOnBottom && <PartnerMessage mb={2} />}
      <Wrapper subtitle={subtitle} {...rest}>
        {toProductsWithLimit(products, maxItems).map(({ product, prices }) => (
          <ProductItem
            key={product.productUrl}
            gaEventCategory={gaEventCategory}
            product={product}
            prices={prices}
            utmMedium={utmMedium}
          />
        ))}
        <Flex justifyContent="center" py={6} px={6} full>
          {expandable &&
          !isExpanded &&
          products.length > NOT_EXPANDED_PRODUCTS_LIMIT ? (
            <Button type="secondary" onClick={onShowAllItems}>
              {COMPARAI.SEE_MORE}
            </Button>
          ) : (
            <Button
              as="a"
              type={expandable ? 'secondary' : 'primary'}
              target="_blank"
              rel="noopener"
              href={`${COMPARAI_URL_WITH_SEARCH_QUERY}${productSlug}`}
              iconRight={<Icon name="link-blank" />}
              onClick={() =>
                sendEvent({
                  action: 'compare_offers',
                  category: gaEventCategory,
                })
              }
            >
              {COMPARAI.COMPARE_PRICES}
            </Button>
          )}
        </Flex>
      </Wrapper>
      {partnerMessageOnBottom && <PartnerMessage mt={2} />}
    </Block>
  );
};

ProductList.defaultProps = {
  expandable: false,
  subtitle: null,
  partnerMessageOnBottom: false,
};

ProductList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  utmMedium: PropTypes.string.isRequired,
  expandable: PropTypes.bool,
  subtitle: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.shape({
        productTitle: PropTypes.string.isRequired,
        productImage: PropTypes.string.isRequired,
        productUrl: PropTypes.string.isRequired,
      }),
      prices: PropTypes.arrayOf(
        PropTypes.shape({
          priceParcels: PropTypes.string,
          priceValue: PropTypes.string.isRequired,
          productTitle: PropTypes.string.isRequired,
          productImage: PropTypes.string.isRequired,
          redirectUrl: PropTypes.string.isRequired,
          storeImage: PropTypes.string.isRequired,
          storeTitle: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  productSlug: PropTypes.string.isRequired,
  partnerMessageOnBottom: PropTypes.bool,
};

export default ProductList;
